import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  Box,
} from '@mui/material';

import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getLCLeadership,
  getElectionOfOfficersStatus,
  postLCLeadership,
  patchLCLeadership,
  deleteLCLeadership,
  replaceExistingLCLeadership,
  LCLeadershipData,
  LCLeadershipRequest,
  LCLeadershipUpdateRequest,
  LCLeadershipReplaceRequest,
  LCLeadershipDeleteRequest,
} from '../../../endpoints/chaptersTs';
import PositionDisplay from '../../../Components/PositionDisplay';
import DelegatesSection from './DelegatesSection';
import { config } from '../../../utils/config';
import dayjs from 'dayjs';

interface LCLeadershipProps {
  id: string;
  lcAccess: boolean;
  lcAction?: boolean;
}

interface SaveData {
  id: string;
  positionId?: string;
  committeeId?: string;
  action: 'newAppointment' | 'editDates' | 'removeRecord';
  userId?: string;
  startDate?: string | null;
  endDate?: string | null;
}

const LCLeadership: React.FC<LCLeadershipProps> = ({
  id,
  lcAccess,
  lcAction = false,
}) => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorSaving, setErrorSaving] = useState<string | null>(null);

  const { data: electionStatus } = useApiGet(
    ['election-of-officers-status', id],
    getElectionOfOfficersStatus,
    { enabled: !!id, retry: 1 },
    id
  );

  // Helper function to format date to YYYY-MM-DD format
  const formatDateForAPI = (
    dateString: string | null | undefined
  ): string | null => {
    if (!dateString) return null;
    const date = dayjs(dateString);
    return date.isValid() ? date.format('YYYY-MM-DD') : null;
  };

  const {
    data: leadershipData,
    isFetching,
    refetch,
  } = useApiGet<LCLeadershipData>(
    ['lc-leadership', id],
    getLCLeadership,
    {
      enabled: true,
      retry: 1,
    },
    { id }
  );

  const { mutate: updateCommittee } = useApiSend(
    patchLCLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: any) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: deleteCommittee } = useApiSend(
    deleteLCLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: any) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: postCommittee } = useApiSend(
    postLCLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: any) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: replaceCommittee } = useApiSend(
    replaceExistingLCLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: any) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const onSavePosition = (position: SaveData) => {
    setIsSaving(true);
    setErrorSaving(null);

    if (position.action === 'newAppointment') {
      //Handle post
      if (!position.id) {
        //Handle new appointment
        const requestData: LCLeadershipRequest = {
          formId: id,
          user_id: position.userId || '',
          start_date: formatDateForAPI(position.startDate) || '',
          end_date: null,
          position_id: position.positionId || '',
          committee_id: position.committeeId || '',
        };
        postCommittee(requestData);
      } else {
        const replaceData: LCLeadershipReplaceRequest = {
          formId: id,
          id: position.id,
          user_id: position.userId || '',
          start_date: formatDateForAPI(position.startDate) || '',
          end_date: null,
        };
        replaceCommittee(replaceData);
      }
    } else if (position.action === 'editDates') {
      //Handle patch
      const updateData: LCLeadershipUpdateRequest = {
        formId: id,
        id: position.id,
        start_date: formatDateForAPI(position.startDate) || '',
        end_date: formatDateForAPI(position.endDate),
      };
      updateCommittee(updateData);
    } else if (position.action === 'removeRecord') {
      //Handle delete
      const deleteData: LCLeadershipDeleteRequest = {
        formId: id,
        id: position.id,
      };
      deleteCommittee(deleteData);
    }
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!leadershipData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  const canEdit = (): boolean => {
    return lcAccess && lcAction;
  };

  const showElectionButton =
    config.ANNUAL_REPORTS_ENABLED &&
    electionStatus?.status === 'pending' &&
    lcAction &&
    !!id;

  return (
    <Grid container>
      <Box sx={{ width: '100%', px: 2, pb: 2 }}>
        {showElectionButton && (
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              navigate(`/local-chapters/${id}/election-of-officers`)
            }
          >
            Report This Year&apos;s Officers
          </Button>
        )}
        {electionStatus?.status === 'completed' && (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            This year&apos;s officers have been reported.
          </Typography>
        )}
      </Box>
      <Grid item xs={12} md={6} sx={{ padding: 2 }}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Current Officers" />
          <CardContent>
            <Grid container spacing={2}>
              {leadershipData.officers
                .sort((a, b) => a.order - b.order)
                .map((user) => (
                  <PositionDisplay
                    id={user.id}
                    position={user.position}
                    positionId={user.position_id}
                    committeeId={user.committee_id}
                    name={user.name}
                    startDate={user.start_date}
                    endDate={user.end_date}
                    canEdit={canEdit()}
                    isSaving={isSaving}
                    errorSavingText={errorSaving}
                    onSave={onSavePosition}
                    userId={user.user_id}
                    pageFrom={'lc-leadership'}
                    key={user.position_id}
                    localChapterId={id}
                  />
                ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      {leadershipData.project_chairs.length > 0 && (
        <Grid item xs={12} md={6} sx={{ padding: 2 }}>
          <Grid item container xs={12}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader title="Project Chairs" sx={{ p: 1 }} />
              <CardContent sx={{ p: 1 }}>
                <Grid container spacing={2}>
                  {leadershipData.project_chairs.map((user) => (
                    <PositionDisplay
                      id={user.id}
                      position={user.position}
                      positionId={user.position_id}
                      committeeId={user.committee_id}
                      name={user.name}
                      startDate={user.start_date}
                      endDate={user.end_date}
                      canEdit={canEdit()}
                      isSaving={isSaving}
                      errorSavingText={errorSaving}
                      onSave={onSavePosition}
                      userId={user.user_id}
                      pageFrom={'lc-leadership'}
                      key={user.position_id}
                      localChapterId={id}
                    />
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Delegates Section - only show if feature enabled and reporting exists */}
      {config.ANNUAL_REPORTS_ENABLED && leadershipData.delegate_reporting && (
        <DelegatesSection
          chapterId={id}
          delegates={leadershipData.delegates || []}
          alternates={leadershipData.alternates || []}
          reporting={leadershipData.delegate_reporting}
          canEdit={canEdit()}
          onRefresh={refetch}
        />
      )}
    </Grid>
  );
};

export default LCLeadership;
